// Anne Rollo Golf - Modern Theme JavaScript

// Theme Module
const AnneRolloTheme = (() => {
  'use strict';

  // Configuration
  const config = {
    breakpoints: {
      mobile: 768,
      tablet: 1024,
      desktop: 1280
    },
    animation: {
      duration: 300,
      easing: 'cubic-bezier(0.4, 0, 0.2, 1)'
    }
  };

  // State Management
  const state = {
    cart: {
      items: [],
      isOpen: false
    },
    mobile: {
      menuOpen: false
    },
    filters: {
      active: []
    },
    megaMenu: {
      activeTrigger: null,
      activeMenu: null,
      documentListenerBound: false
    }
  };

  // DOM Cache
  const elements = {};

  // Initialize Theme
  function init() {
    cacheDom();
    bindEvents();
    initLazyLoading();
    initAnimations();
    initCart();
    initAccessibility();
    initMegaMenu();
    initTestimonialSlider();
    initLeadPopup();
  }

  // Cache DOM Elements
  function cacheDom() {
    elements.header = document.querySelector('.site-header');
    elements.mobileMenuToggle = document.querySelector('.mobile-nav-toggle'); // Updated selector
    elements.mobileMenu = document.querySelector('.mobile-nav-panel'); // Updated selector
    elements.mobileMenuOverlay = document.querySelector('.mobile-nav-overlay'); // Updated selector
    elements.cartDrawer = document.querySelector('.cart-drawer');
    elements.cartCount = document.querySelector('.cart-count');
    elements.productCards = document.querySelectorAll('.product-card');
    elements.addToCartForms = document.querySelectorAll('.product-form');
    elements.megaTriggers = document.querySelectorAll('[data-mega-trigger]');
    elements.megaMenus = document.querySelectorAll('[data-mega-menu]');
  }

  // Bind Events
  function bindEvents() {
    // Mobile Menu
    if (elements.mobileMenuToggle) {
      elements.mobileMenuToggle.addEventListener('click', () => {
        toggleMobileMenu();
      });
    }
    if (elements.mobileMenuOverlay) {
        elements.mobileMenuOverlay.addEventListener('click', () => toggleMobileMenu(false));
    }

    // Cart Events
    document.addEventListener('click', (e) => {
      const cartToggle = e.target.closest('.cart-toggle');
      if (cartToggle) {
        e.preventDefault();
        toggleCart();
        return;
      }

      const quickAddButton = e.target.closest('.quick-add-btn[data-product-id]');
      if (quickAddButton) {
        handleQuickAdd(e, quickAddButton);
      }
    });

    // Form Submissions
    elements.addToCartForms?.forEach(form => {
      if (form.dataset.ajaxCartBound === 'true') return;
      form.addEventListener('submit', handleAddToCart);
      form.dataset.ajaxCartBound = 'true';
    });

    // Scroll Events
    let lastScroll = 0;
    window.addEventListener('scroll', throttle(() => {
      handleScroll(lastScroll);
      lastScroll = window.pageYOffset;
    }, 100));

    // Resize Events
    window.addEventListener('resize', debounce(() => {
      handleResize();
    }, 250));
  }

  // Mega menu interactions for desktop navigation
  function initMegaMenu() {
    if (!elements.megaTriggers || elements.megaTriggers.length === 0) return;

    elements.megaTriggers.forEach((trigger) => {
      const menuId = trigger.getAttribute('aria-controls');
      if (!menuId) return;

      const menu = document.getElementById(menuId);
      if (!menu) return;

      const items = Array.from(menu.querySelectorAll('.mega-menu__item'));
      const open = () => openMegaMenu(trigger, menu);
      const close = () => closeMegaMenu(trigger, menu);

      trigger.addEventListener('mouseenter', open);
      trigger.addEventListener('focus', open);

      trigger.addEventListener('mouseleave', () => {
        window.setTimeout(() => {
          if (!menu.matches(':hover')) {
            close();
          }
        }, 90);
      });

      trigger.addEventListener('blur', () => {
        window.setTimeout(() => {
          if (!menu.contains(document.activeElement)) {
            close();
          }
        }, 90);
      });

      menu.addEventListener('mouseenter', open);

      menu.addEventListener('mouseleave', () => {
        window.setTimeout(() => {
          if (!trigger.matches(':hover')) {
            close();
          }
        }, 90);
      });

      menu.addEventListener('focusout', (event) => {
        if (!menu.contains(event.relatedTarget) && event.relatedTarget !== trigger) {
          close();
        }
      });

      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          close();
          trigger.blur();
        }

        if (event.key === 'ArrowDown') {
          event.preventDefault();
          items[0]?.focus();
        }
      });

      items.forEach((item, index) => {
        item.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            const nextIndex = (index + 1) % items.length;
            items[nextIndex]?.focus();
          }

          if (event.key === 'ArrowUp') {
            event.preventDefault();
            const prevIndex = (index - 1 + items.length) % items.length;
            items[prevIndex]?.focus();
          }

          if (event.key === 'Escape') {
            close();
            trigger.focus();
          }
        });
      });
    });

    if (!state.megaMenu.documentListenerBound) {
      document.addEventListener('click', handleMegaOutsideClick, true);
      state.megaMenu.documentListenerBound = true;
    }
  }

  function openMegaMenu(trigger, menu) {
    if (!trigger || !menu) return;

    if (state.megaMenu.activeMenu && state.megaMenu.activeMenu !== menu) {
      closeMegaMenu();
    }

    trigger.setAttribute('aria-expanded', 'true');
    menu.classList.add('is-open');
    state.megaMenu.activeTrigger = trigger;
    state.megaMenu.activeMenu = menu;
  }

  function closeMegaMenu(trigger = state.megaMenu.activeTrigger, menu = state.megaMenu.activeMenu) {
    if (!trigger || !menu) return;

    trigger.setAttribute('aria-expanded', 'false');
    menu.classList.remove('is-open');

    if (state.megaMenu.activeTrigger === trigger) {
      state.megaMenu.activeTrigger = null;
      state.megaMenu.activeMenu = null;
    }
  }

  function handleMegaOutsideClick(event) {
    const { activeMenu, activeTrigger } = state.megaMenu;
    if (!activeMenu || !activeTrigger) return;

    if (!activeMenu.contains(event.target) && !activeTrigger.contains(event.target)) {
      closeMegaMenu();
    }
  }

  // Lead Magnet Pop-up
  function initLeadPopup() {
    const popup = document.querySelector('[data-lead-popup]');
    if (!popup) return;

    const storageKey = 'anneRolloLeadPopupDismissed';
    const viewsKey = 'anneRolloLeadPopupViews';
    const threshold = parseInt(popup.dataset.pageViewThreshold, 10) || 5;

    const hasDismissed = localStorage.getItem(storageKey) === 'true';
    let pageViews = parseInt(localStorage.getItem(viewsKey) || '0', 10);
    pageViews += 1;
    localStorage.setItem(viewsKey, pageViews.toString());

    if (hasDismissed) return;

    let popupShown = false;

    const openPopup = () => {
      if (popupShown || document.body.classList.contains('popup-open')) return;
      popup.classList.add('is-open');
      popup.setAttribute('aria-hidden', 'false');
      document.body.classList.add('popup-open');
      popupShown = true;
    };

    const closePopup = (persist = true) => {
      popup.classList.remove('is-open');
      popup.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('popup-open');
      if (persist) {
        localStorage.setItem(storageKey, 'true');
      }
    };

    popup.querySelectorAll('[data-lead-popup-close]').forEach((btn) => {
      btn.addEventListener('click', () => closePopup());
    });

    const form = popup.querySelector('form');
    if (form) {
      form.addEventListener('submit', () => {
        localStorage.setItem(storageKey, 'true');
      });
    }

    if (pageViews >= threshold) {
      window.setTimeout(openPopup, 2500);
    }

    const handleExitIntent = (event) => {
      if (event.clientY <= 0) {
        openPopup();
        document.removeEventListener('mouseout', handleExitIntent);
      }
    };

    document.addEventListener('mouseout', handleExitIntent);
  }

  // Cart Functions
  function initCart() {
    fetchCart();
  }

  async function fetchCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      updateCartUI(cart);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }

  async function handleAddToCart(e) {
    e.preventDefault();
    const form = e.target;
    const submitBtn = form.querySelector('[type="submit"]');
    const originalButtonHTML = submitBtn ? submitBtn.innerHTML : '';

    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-busy', 'true');
      submitBtn.innerHTML = '<span class="spinner"></span> Adding...';
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      await response.json();
      await fetchCart();
      toggleCart(true);
      showNotification('Product added to cart!', 'success');
    } catch (error) {
      console.error('Error adding to cart:', error);
      showNotification('Error adding to cart', 'error');
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.innerHTML = originalButtonHTML || 'Add to Cart';
        submitBtn.removeAttribute('aria-busy');
      }
    }
  }

  async function handleQuickAdd(e, button) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    let btn = button;
    if (!btn && e && e.target && typeof e.target.closest === 'function') {
      btn = e.target.closest('.quick-add-btn[data-product-id]');
    }
    if (!btn) return;

    const productId = btn.dataset.productId;
    const variantId = btn.dataset.variantId || productId;

    if (!variantId) return;

    const originalButtonHTML = btn.innerHTML;
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    btn.innerHTML = '<span class="spinner"></span>';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      });

      if (!response.ok) throw new Error('Failed to add to cart');

      await fetchCart();
      toggleCart(true);
      showNotification('Product added to cart!', 'success');
    } catch (error) {
      console.error('Error with quick add:', error);
      showNotification('Error adding to cart', 'error');
    } finally {
      btn.disabled = false;
      btn.innerHTML = originalButtonHTML;
      btn.removeAttribute('aria-busy');
    }
  }

  async function updateCartItem(key, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity
        })
      });

      if (!response.ok) throw new Error('Failed to update cart');

      const cart = await response.json();
      updateCartUI(cart);
    } catch (error) {
      console.error('Error updating cart:', error);
      showNotification('Error updating cart', 'error');
    }
  }

  function updateCartUI(cart) {
    state.cart.items = cart.items;
    
    // Update cart count
    if (elements.cartCount) {
      elements.cartCount.textContent = cart.item_count;
      elements.cartCount.style.display = cart.item_count > 0 ? 'flex' : 'none';
    }

    // Update cart drawer
    if (elements.cartDrawer) {
      renderCartDrawer(cart);
    }
  }

  function renderCartDrawer(cart) {
    const cartItemsContainer = elements.cartDrawer.querySelector('.cart-items');
    const emptyCartEl = elements.cartDrawer.querySelector('.empty-cart');
    const cartFooter = elements.cartDrawer.querySelector('.cart-footer');
    const cartDrawerCount = elements.cartDrawer.querySelector('.cart-drawer-count');

    if (!cartItemsContainer) return;

    // Update cart count in drawer header
    if (cartDrawerCount) {
      cartDrawerCount.textContent = cart.item_count;
    }

    if (cart.items.length === 0) {
      // Hide cart items and footer, show empty state
      cartItemsContainer.innerHTML = '';
      if (cartFooter) cartFooter.style.display = 'none';
      if (emptyCartEl) emptyCartEl.style.display = 'flex';
      return;
    }

    // Show cart items and footer, hide empty state
    if (cartFooter) cartFooter.style.display = 'block';
    if (emptyCartEl) emptyCartEl.style.display = 'none';

    let cartHTML = '';

    cart.items.forEach(item => {
      cartHTML += `
        <div class="cart-item" data-key="${item.key}">
          <div class="cart-item-image">
            <img src="${item.image}" alt="${item.title}">
          </div>
          <div class="cart-item-details">
            <div class="cart-item-title">${item.title}</div>
            ${item.variant_title ? `<div class="cart-item-variant">${item.variant_title}</div>` : ''}
            <div class="cart-item-price">${formatMoney(item.line_price)}</div>
            <div class="cart-item-quantity">
              <div class="quantity-controls">
                <button class="quantity-btn" data-action="decrease" data-key="${item.key}" aria-label="Decrease quantity">−</button>
                <span class="quantity-value">${item.quantity}</span>
                <button class="quantity-btn" data-action="increase" data-key="${item.key}" aria-label="Increase quantity">+</button>
              </div>
              <button class="cart-item-remove" data-key="${item.key}" aria-label="Remove ${item.title}">Remove</button>
            </div>
          </div>
        </div>
      `;
    });

    cartItemsContainer.innerHTML = cartHTML;

    // Update cart footer with subtotal
    if (cartFooter) {
      const subtotalAmount = cartFooter.querySelector('.cart-subtotal-amount');
      if (subtotalAmount) {
        subtotalAmount.textContent = formatMoney(cart.total_price);
      }
    }

    // Bind cart item events
    cartItemsContainer.querySelectorAll('.quantity-btn').forEach(btn => {
      btn.addEventListener('click', handleQuantityChange);
    });

    cartItemsContainer.querySelectorAll('.cart-item-remove').forEach(btn => {
      btn.addEventListener('click', handleRemoveItem);
    });
  }

  function handleQuantityChange(e) {
    const action = e.target.dataset.action;
    const key = e.target.dataset.key;
    const quantitySpan = e.target.parentElement.querySelector('.quantity-value');
    let quantity = parseInt(quantitySpan.textContent);

    if (action === 'increase') {
      quantity++;
    } else if (action === 'decrease' && quantity > 1) {
      quantity--;
    }

    updateCartItem(key, quantity);
  }

  function handleRemoveItem(e) {
    const key = e.target.dataset.key;
    updateCartItem(key, 0);
  }

  function toggleCart(open = null) {
    const shouldOpen = open !== null ? open : !state.cart.isOpen;
    state.cart.isOpen = shouldOpen;

    if (elements.cartDrawer) {
      elements.cartDrawer.classList.toggle('active', shouldOpen);
      document.body.classList.toggle('cart-open', shouldOpen);
    }

    // Add overlay
    let overlay = document.querySelector('.cart-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'cart-overlay';
      document.body.appendChild(overlay);
    }
    overlay.classList.toggle('active', shouldOpen);
    
    if (shouldOpen) {
      overlay.addEventListener('click', () => toggleCart(false));
    }
  }

  // Mobile Menu
  function toggleMobileMenu(open = null) {
    const shouldOpen = open !== null ? open : !document.body.classList.contains('mobile-nav-is-open');
    document.body.classList.toggle('mobile-nav-is-open', shouldOpen);

    if (state.megaMenu.activeMenu) {
      closeMegaMenu();
    }
  }

  // Scroll Handling
  function handleScroll(lastScroll) {
    const currentScroll = window.pageYOffset;
    
    if (elements.header) {
      // Add scrolled class
      elements.header.classList.toggle('scrolled', currentScroll > 50);
      
      // Hide/show on scroll
      if (currentScroll > lastScroll && currentScroll > 100) {
        elements.header.classList.add('hidden');
      } else {
        elements.header.classList.remove('hidden');
      }
    }
  }

  // Resize Handling
  function handleResize() {
    const isMobile = window.innerWidth <= config.breakpoints.mobile;
    
    // Close mobile menu on resize to desktop
    if (!isMobile && document.body.classList.contains('mobile-nav-is-open')) {
      toggleMobileMenu(false);
    }

    if (isMobile && state.megaMenu.activeMenu) {
      closeMegaMenu();
    }
  }

  // Lazy Loading
  function initLazyLoading() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            img.src = img.dataset.src;
            img.classList.add('loaded');
            imageObserver.unobserve(img);
          }
        });
      });

      document.querySelectorAll('img[data-src]').forEach(img => {
        imageObserver.observe(img);
      });
    }
  }

  // Animations
  function initAnimations() {
    if ('IntersectionObserver' in window) {
      const animationObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animated');
            animationObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.1 });

      document.querySelectorAll('[data-animate]').forEach(el => {
        animationObserver.observe(el);
      });
    }

    // Initialize counter animations
    initCounterAnimations();
    
    // Initialize testimonial slider
    initTestimonialSlider();
  }

  // Counter Animation for Statistics
  function initCounterAnimations() {
    const observerOptions = {
      threshold: 0.5,
      rootMargin: '0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const counter = entry.target;
          const target = parseInt(counter.dataset.count);
          const duration = 2000;
          const increment = target / (duration / 16);
          let current = 0;
          
          const updateCounter = () => {
            current += increment;
            if (current < target) {
              counter.textContent = Math.floor(current).toLocaleString();
              requestAnimationFrame(updateCounter);
            } else {
              counter.textContent = target.toLocaleString();
            }
          };
          
          updateCounter();
          observer.unobserve(counter);
        }
      });
    }, observerOptions);

    document.querySelectorAll('.stat-number').forEach(counter => {
      observer.observe(counter);
    });
  }

  // Testimonial Slider
  function initTestimonialSlider() {
    const slider = document.querySelector('.testimonials-slider');
    if (!slider) return;

    const slides = slider.querySelectorAll('.testimonial-slide');
    if (slides.length <= 1) return;

    let currentSlide = 0;
    const slideInterval = 5000; // 5 seconds

    function showSlide(index) {
      slides.forEach((slide, i) => {
        slide.classList.toggle('active', i === index);
      });
    }

    function nextSlide() {
      currentSlide = (currentSlide + 1) % slides.length;
      showSlide(currentSlide);
    }

    // Auto-advance slides
    setInterval(nextSlide, slideInterval);

    // Initialize first slide
    showSlide(0);
  }

  // Accessibility
  function initAccessibility() {
    // Focus trap for modals
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        if (state.cart.isOpen) toggleCart(false);
        if (document.body.classList.contains('mobile-nav-is-open')) toggleMobileMenu(false);
      }
    });

    // Skip to content
    const skipLink = document.querySelector('.skip-to-content');
    if (skipLink) {
      skipLink.addEventListener('click', (e) => {
        e.preventDefault();
        const target = document.querySelector(skipLink.getAttribute('href'));
        if (target) {
          target.tabIndex = -1;
          target.focus();
        }
      });
    }
  }

  // Utility Functions
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2) + ' AUD';
  }

  function showNotification(message, type = 'info') {
    const notification = document.createElement('div');
    notification.className = `notification notification-${type} slide-up`;
    notification.textContent = message;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.classList.add('fade-out');
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  function throttle(func, limit) {
    let inThrottle;
    return function(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  // Public API
  return {
    init,
    toggleCart,
    toggleMobileMenu,
    showNotification
  };
})();

// Expose cart controls for inline handlers and other scripts
window.AnneRolloTheme = AnneRolloTheme;
window.toggleCart = AnneRolloTheme.toggleCart;

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', AnneRolloTheme.init);
} else {
  AnneRolloTheme.init();
}

// PRODUCT THUMBNAIL FIX - Override everything
(function() {
  'use strict';

  function fixProductThumbnails() {
    console.log('[THUMBNAIL FIX] Starting fix...');

    const mainImage = document.getElementById('main-product-image');
    const thumbnails = document.querySelectorAll('.thumbnail-btn');

    if (!mainImage || thumbnails.length === 0) {
      console.log('[THUMBNAIL FIX] Waiting for elements...');
      setTimeout(fixProductThumbnails, 500);
      return;
    }

    console.log('[THUMBNAIL FIX] Found ' + thumbnails.length + ' thumbnails');

    // Remove ALL existing event listeners by cloning
    thumbnails.forEach(function(btn) {
      const newBtn = btn.cloneNode(true);
      btn.parentNode.replaceChild(newBtn, btn);
    });

    // Get fresh references after cloning
    const freshThumbnails = document.querySelectorAll('.thumbnail-btn');

    // Add our click handler
    freshThumbnails.forEach(function(btn, index) {
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        console.log('[THUMBNAIL FIX] Click on thumbnail ' + index);

        // Try multiple ways to get the image URL
        let imageUrl = null;

        // Method 1: From data attribute
        imageUrl = btn.getAttribute('data-full-image') ||
                   btn.getAttribute('data-image-src') ||
                   btn.dataset.fullImage ||
                   btn.dataset.imageSrc;

        // Method 2: From the thumbnail img src
        if (!imageUrl) {
          const thumbImg = btn.querySelector('img');
          if (thumbImg) {
            imageUrl = thumbImg.src
              .replace('_100x100', '_800x800')
              .replace('_100x', '_800x')
              .replace('width=100', 'width=800')
              .replace('/100/', '/800/');
          }
        }

        if (imageUrl) {
          // Fix URL if it starts with //
          if (imageUrl.startsWith('//')) {
            imageUrl = 'https:' + imageUrl;
          }

          console.log('[THUMBNAIL FIX] Changing to: ' + imageUrl);

          // AGGRESSIVE FIX - Force update ALL possible image locations
          // Update the main image
          mainImage.src = imageUrl;
          mainImage.setAttribute('src', imageUrl);

          // Also update srcset if it exists
          mainImage.setAttribute('srcset', imageUrl);

          // Force browser to reload the image
          mainImage.style.display = 'none';
          setTimeout(function() {
            mainImage.style.display = 'block';
          }, 10);

          // Also check if there's a picture element and update its sources
          const picture = mainImage.closest('picture');
          if (picture) {
            const sources = picture.querySelectorAll('source');
            sources.forEach(function(source) {
              source.srcset = imageUrl;
            });
          }

          // Check if there are multiple images with same ID (duplicate elements)
          const allMainImages = document.querySelectorAll('#main-product-image');
          allMainImages.forEach(function(img) {
            img.src = imageUrl;
            img.setAttribute('src', imageUrl);
          });

          // Update active states
          freshThumbnails.forEach(function(b) {
            b.classList.remove('active');
          });
          btn.classList.add('active');

          console.log('[THUMBNAIL FIX] Success! Updated ' + allMainImages.length + ' image(s) to: ' + imageUrl);
        } else {
          console.error('[THUMBNAIL FIX] Could not find image URL');
        }

        return false;
      }, true);
    });

    console.log('[THUMBNAIL FIX] Fix applied!');
  }

  // Try multiple times to ensure it works
  setTimeout(fixProductThumbnails, 100);
  setTimeout(fixProductThumbnails, 500);
  setTimeout(fixProductThumbnails, 1000);

  // Also run on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fixProductThumbnails);
  } else {
    fixProductThumbnails();
  }
})();
